import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { useAuth } from "../context/AuthContext";
import { useAuthModal } from "../context/AuthModalContext";
import { LogoMark } from "./brand/Logo";
import { errMsg } from "../lib/api";

const Field = ({ label, children, right }) => (
  <label className="block space-y-1.5">
    <span className="flex items-center justify-between text-xs font-medium text-slate-500">{label}{right}</span>
    {children}
  </label>
);

const inputCls = "w-full h-11 px-3.5 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-200 transition-colors";

const AuthModal = () => {
  const { mode, close, switchMode, openForgot } = useAuthModal();
  const { signup, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const open = mode === "login" || mode === "signup";
  const isSignup = mode === "signup";
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (isSignup && form.name.trim().length < 2) return setError("Please enter your name");
    if (!form.email.includes("@")) return setError("Please enter a valid email");
    if (form.password.length < 6) return setError("Password must be at least 6 characters");
    setBusy(true);
    try {
      const u = isSignup
        ? await signup({ name: form.name.trim(), email: form.email.trim(), password: form.password })
        : await login({ email: form.email.trim(), password: form.password });
      toast.success(isSignup ? `Welcome to Ishukart, ${u.name.split(" ")[0]}!` : `Welcome back, ${u.name.split(" ")[0]}`);
      setForm({ name: "", email: "", password: "" });
      close();
      navigate("/home");
    } catch (err) {
      setError(errMsg(err, "Could not sign you in"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setError(""); close(); } }}>
      <DialogContent className="sm:max-w-[420px] bg-white border-slate-200 p-0 overflow-hidden" data-testid="auth-modal">
        <div className="relative px-7 pt-7 pb-5 border-b border-slate-200 grid-bg">
          <LogoMark size={34} />
          <DialogTitle className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900">
            {isSignup ? "Create your account" : "Welcome back"}
          </DialogTitle>
          <DialogDescription className="mt-1 text-sm text-slate-500">
            {isSignup ? "Describe your idea and watch it come to life." : "Log in to continue building."}
          </DialogDescription>
        </div>
        <form onSubmit={onSubmit} className="px-7 py-6 space-y-4">
          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-600" data-testid="auth-error">
              <AlertCircle size={14} className="mt-0.5 shrink-0" /> {error}
            </div>
          )}
          {isSignup && (
            <Field label="Name">
              <input value={form.name} onChange={set("name")} placeholder="Ada Lovelace" className={inputCls} data-testid="auth-name-input" autoComplete="name" />
            </Field>
          )}
          <Field label="Email">
            <input type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} data-testid="auth-email-input" autoComplete="email" />
          </Field>
          <Field
            label="Password"
            right={!isSignup && (
              <button type="button" onClick={openForgot} className="text-emerald-600 hover:text-emerald-700" data-testid="auth-forgot-link">Forgot password?</button>
            )}
          >
            <div className="relative">
              <input type={showPw ? "text" : "password"} value={form.password} onChange={set("password")} placeholder="At least 6 characters" className={`${inputCls} pr-10`} data-testid="auth-password-input" autoComplete={isSignup ? "new-password" : "current-password"} />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900" data-testid="auth-toggle-password" aria-label="Toggle password visibility">
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </Field>
          <button type="submit" disabled={busy} data-testid="login-submit-button" className="w-full h-11 rounded-lg bg-slate-900 text-white font-semibold text-sm hover:bg-slate-700 disabled:opacity-60 transition-colors flex items-center justify-center gap-2">
            {busy && <Loader2 size={15} className="animate-spin" />} {isSignup ? "Create account" : "Log in"}
          </button>
          <p className="text-center text-xs text-slate-500">
            {isSignup ? "Already have an account? " : "New to Ishukart? "}
            <button type="button" onClick={() => { setError(""); switchMode(isSignup ? "login" : "signup"); }} className="text-emerald-600 hover:text-emerald-700 font-medium" data-testid="auth-switch-mode">
              {isSignup ? "Log in" : "Create an account"}
            </button>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
